import React, { ReactNode } from 'react';
import classNames from 'classnames';
import KSButton, { type KSButtonVariant } from '../KSButton/KSButton';
import './EmptyState.scss';

export interface EmptyStateActionConfig {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Button variant (default: 'secondary') */
  variant?: KSButtonVariant;
  /** Optional icon */
  icon?: ReactNode;
}

export interface EmptyStateProps {
  /** Icon to display above the title */
  icon?: ReactNode;
  /** Main message/title */
  title: string;
  /** Optional description text */
  description?: string;
  /** Optional action button configuration */
  action?: EmptyStateActionConfig;
  /** Optional secondary action button configuration */
  secondaryAction?: EmptyStateActionConfig;
  /** Visual variant */
  variant?: 'default' | 'subtle';
  /** Disabled state - muted appearance with different background */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
}

/**
 * EmptyState - Reusable empty state component for KSQ2 panels
 *
 * Used when:
 * - No items match the current filter
 * - No item is selected
 * - A list is empty
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  secondaryAction,
  variant = 'default',
  disabled = false,
  className,
}) => {
  return (
    <div
      className={classNames(
        'ksq2-empty-state',
        `ksq2-empty-state--${variant}`,
        { 'ksq2-empty-state--disabled': disabled },
        className
      )}
    >
      {icon && <div className="ksq2-empty-state__icon">{icon}</div>}
      <div className="ksq2-empty-state__content">
        <p className="ksq2-empty-state__title">{title}</p>
        {description && <p className="ksq2-empty-state__description">{description}</p>}
      </div>
      {(action || secondaryAction) && (
        <div className="ksq2-empty-state__actions">
          {action && (
            <div className="ksq2-empty-state__action">
              <KSButton
                label={action.label}
                onClick={action.onClick}
                variant={action.variant ?? 'secondary'}
                icon={action.icon}
                iconPosition="right"
                pill
              />
            </div>
          )}
          {secondaryAction && (
            <div className="ksq2-empty-state__action">
              <KSButton
                label={secondaryAction.label}
                onClick={secondaryAction.onClick}
                variant={secondaryAction.variant ?? 'secondary'}
                icon={secondaryAction.icon}
                iconPosition="right"
                pill
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
